import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../assets/logo.jpeg';

export const generateInvoicePDF = async (order, shop, bde, action = 'download') => {
  try {
    if (!order) throw new Error("Order data is missing");
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
  const margin = 15;
  const primaryColor = [41, 128, 185]; // #2980b9
  const grayColor = [100, 100, 100];
  const lightGray = [240, 240, 240];

  // Helper to load image
  const loadImage = (src) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
    });
  };

  // --- 1. Header Section ---
  const logoImg = await loadImage(logo);
  if (logoImg) {
    doc.addImage(logoImg, 'JPEG', margin, 15, 30, 30);
  }

  // Invoice Title & Details (Top Right)
  const invNo = order.invoiceNumber || `INV-${order.id.split('-')[1] || 'PENDING'}`;
  const invDate = order.invoiceDate || order.createdAt || new Date().toISOString().split('T')[0];

  doc.setFontSize(26);
  doc.setTextColor(...primaryColor);
  doc.setFont(undefined, 'bold');
  doc.text("INVOICE", pageWidth - margin, 25, { align: 'right' });

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.text(`# ${invNo}`, pageWidth - margin, 32, { align: 'right' });
  doc.text(`Date: ${invDate}`, pageWidth - margin, 37, { align: 'right' });
  if (order.status) {
    doc.setTextColor(...grayColor);
    doc.text(`Status: ${order.status}`, pageWidth - margin, 42, { align: 'right' });
  }

  // --- 2. Company Info (From) ---
  let yPos = 55;
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text("B2B Sales System Pvt Ltd", margin, yPos);

  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...grayColor);
  yPos += 5;
  doc.text("Vidyagiri, 14 cross Bagalkot,", margin, yPos);
  yPos += 4;
  doc.text("Bagalkot Karnataka, 587101", margin, yPos);
  yPos += 4;
  doc.text("Email: support@b2bsales.com", margin, yPos);
  yPos += 4;
  doc.text("GSTIN: 29CUNPA8415L1Z5", margin, yPos);

  // --- 3. Bill To Section (Right Side Box) ---
  const rightColX = pageWidth / 2 + 10;
  yPos = 55;

  // Background box for Bill To
  doc.setFillColor(...lightGray);
  doc.roundedRect(rightColX - 5, yPos - 8, (pageWidth / 2) - margin - 5, 35, 2, 2, 'F');

  doc.setFontSize(11);
  doc.setTextColor(...primaryColor);
  doc.setFont(undefined, 'bold');
  doc.text("Bill To:", rightColX, yPos);

  yPos += 6;
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(shop?.name || order.customerName || "Walk-in Customer", rightColX, yPos);

  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...grayColor);
  yPos += 5;

  if (shop) {
    doc.text(shop.ownerName || '', rightColX, yPos);
    yPos += 4;
    const address = shop.address || '';
    const splitAddress = doc.splitTextToSize(address, 80);
    doc.text(splitAddress, rightColX, yPos);
    
    yPos += (splitAddress.length * 4) + 2;
    doc.text(`Mobile: ${shop.contactNumber || '-'}`, rightColX, yPos);
    if (shop.gstNo) {
        doc.text(`GSTIN: ${shop.gstNo}`, rightColX, yPos + 4);
    }
  } else {
    doc.text("Address not available", rightColX, yPos);
  }

  // --- 4. Order Meta Bar ---
  const metaY = 95;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, metaY, pageWidth - margin, metaY);
  
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text("Order ID:", margin, metaY + 5);
  doc.setFont(undefined, 'normal');
  doc.text(order.id, margin + 20, metaY + 5);

  if (bde) {
    doc.setFont(undefined, 'bold');
    doc.text("Sales Rep:", margin + 80, metaY + 5);
    doc.setFont(undefined, 'normal');
    doc.text(bde.name || '-', margin + 100, metaY + 5);
  }

  // --- 5. Table Section ---
  const tableColumn = ["#", "Product Description", "Qty", "Rate", "Amount"];
  const tableRows = [];

  const items = order.items && order.items.length > 0 ? order.items : [{
    productName: order.productName,
    quantity: order.quantity,
    mrp: order.mrp, 
    amount: order.billingPrice || order.amount,
  }];

  items.forEach((item, index) => {
    const unitPrice = item.amount / (item.quantity || 1);
    
    const row = [
      index + 1,
      `${item.productName || 'Product'} ${item.volumeMl ? `(${item.volumeMl}ml)` : ''}`,
      item.quantity || 1,
      `Rs. ${unitPrice.toFixed(2)}`,
      `Rs. ${(item.amount || 0).toFixed(2)}`
    ];
    tableRows.push(row);
  });

  autoTable(doc, {
    startY: 110,
    head: [tableColumn],
    body: tableRows,
    theme: 'grid',
    headStyles: { 
        fillColor: primaryColor, 
        textColor: 255, 
        fontStyle: 'bold',
        halign: 'center'
    },
    columnStyles: {
        0: { halign: 'center', cellWidth: 15 }, // #
        1: { cellWidth: 'auto' }, // Product
        2: { halign: 'center', cellWidth: 20 }, // Qty
        3: { halign: 'right', cellWidth: 30 }, // Rate
        4: { halign: 'right', cellWidth: 35 } // Amount
    },
    styles: { 
        fontSize: 10,
        cellPadding: 3,
        valign: 'middle'
    },
    alternateRowStyles: {
        fillColor: [245, 248, 250]
    },
    margin: { top: 110, left: margin, right: margin }
  });

  // --- 6. Totals Section ---
  const finalY = doc.lastAutoTable.finalY + 10;
  const totalXLabel = pageWidth - 80;
  const totalXValue = pageWidth - margin;

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);

  let currentY = finalY;
  const subTotal = order.amount;
  const discount = order.discount || 0;
  const taxableAmount = subTotal - discount;
  const gstAmount = taxableAmount * 0.18;
  const grandTotal = taxableAmount + gstAmount;

  // Subtotal
  doc.text(`Subtotal:`, totalXLabel, currentY);
  doc.text(`Rs. ${subTotal.toFixed(2)}`, totalXValue, currentY, { align: 'right' });
  currentY += 6;

  // Discount
  if (discount > 0) {
      doc.setTextColor(...primaryColor); // Highlight discount
      doc.text(`Discount:`, totalXLabel, currentY);
      doc.text(`- Rs. ${discount.toFixed(2)}`, totalXValue, currentY, { align: 'right' });
      doc.setTextColor(0, 0, 0); // Reset color
      currentY += 6;
  }

  // GST (18%)
  doc.text(`GST (18%):`, totalXLabel, currentY);
  doc.text(`Rs. ${gstAmount.toFixed(2)}`, totalXValue, currentY, { align: 'right' });
  currentY += 6;

  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.line(totalXLabel - 5, currentY + 3, pageWidth - margin, currentY + 3);
  currentY += 8;

  // Grand Total
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text(`Grand Total:`, totalXLabel, currentY);
  doc.text(`Rs. ${grandTotal.toFixed(2)}`, totalXValue, currentY, { align: 'right' });

  // --- 7. Footer ---
  const pageHeight = doc.internal.pageSize.height;
  
  // Terms & Conditions (Optional)
  doc.setFontSize(8);
  doc.setTextColor(...grayColor);
  doc.setFont(undefined, 'normal');
  doc.text("Terms & Conditions:", margin, pageHeight - 35);
  doc.text("1. Goods once sold will not be taken back.", margin, pageHeight - 30);
  doc.text("2. Interest @ 18% p.a. will be charged if payment is not made within due date.", margin, pageHeight - 26);

  // Signatory
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text("Authorized Signatory", pageWidth - margin, pageHeight - 30, { align: 'right' });
  doc.setFontSize(8);
  doc.text("(B2B Sales System Pvt Ltd)", pageWidth - margin, pageHeight - 25, { align: 'right' });

  // Bottom Bar
  doc.setFillColor(...primaryColor);
  doc.rect(0, pageHeight - 5, pageWidth, 5, 'F');

  // --- 8. Action ---
  if (action === 'download') {
    doc.save(`Invoice_${invNo}.pdf`);
  } else if (action === 'share') {
    const pdfBlob = doc.output('blob');
    const file = new File([pdfBlob], `Invoice_${invNo}.pdf`, { type: 'application/pdf' });
    
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: `Invoice ${invNo}`,
        text: `Here is the invoice for your order ${order.id}.`,
      });
    } else {
      throw new Error("Sharing is not supported on this device/browser.");
    }
  }
  
  return true; // Success
  } catch (error) {
    console.error("Invoice Generation Error:", error);
    throw error; // Re-throw for caller to handle
  }
};
